import { useState, useMemo } from 'react'
import { Tool } from '../types/database'

export type PricingFilter = 'free' | 'freemium' | 'paid'

export interface FilterState {
  pricing: PricingFilter[]
  categories: string[]
  minRating: number
  features: string[]
}

export function useFilters(tools: Tool[]) {
  const [filters, setFilters] = useState<FilterState>({
    pricing: [],
    categories: [],
    minRating: 0,
    features: [],
  })

  // Get pricing type from tool
  const getToolPricingType = (tool: Tool): PricingFilter => {
    if (!tool.pricing_tiers || tool.pricing_tiers.length === 0) return 'paid'

    const hasFree = tool.pricing_tiers.some((t) =>
      t.price.toLowerCase().includes('free'),
    )
    const hasPaid = tool.pricing_tiers.some(
      (t) => !t.price.toLowerCase().includes('free'),
    )

    if (hasFree && hasPaid) return 'freemium'
    if (hasFree) return 'free'
    return 'paid'
  }

  // Check if tool belongs to any selected category
  const toolMatchesCategories = (tool: Tool): boolean => {
    if (filters.categories.length === 0) return true

    const toolCategories =
      tool.categories && tool.categories.length > 0
        ? tool.categories
        : [tool.category]

    return toolCategories.some((cat) => filters.categories.includes(cat))
  }

  // Filter tools based on all criteria
  const filteredTools = useMemo(() => {
    return tools.filter((tool) => {
      // Pricing filter
      if (filters.pricing.length > 0) {
        const toolPricing = getToolPricingType(tool)
        if (!filters.pricing.includes(toolPricing)) return false
      }

      // Category filter
      if (!toolMatchesCategories(tool)) return false

      return true
    })
  }, [tools, filters])

  // Get all unique categories from tools
  const availableCategories = useMemo(() => {
    const categoriesSet = new Set<string>()
    tools.forEach((tool) => {
      if (tool.categories && tool.categories.length > 0) {
        tool.categories.forEach((cat) => categoriesSet.add(cat))
      } else {
        categoriesSet.add(tool.category)
      }
    })
    return Array.from(categoriesSet).sort()
  }, [tools])

  // Toggle pricing filter
  const togglePricing = (pricing: PricingFilter) => {
    setFilters((prev) => ({
      ...prev,
      pricing: prev.pricing.includes(pricing)
        ? prev.pricing.filter((p) => p !== pricing)
        : [...prev.pricing, pricing],
    }))
  }

  // Toggle category filter
  const toggleCategory = (category: string) => {
    setFilters((prev) => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter((c) => c !== category)
        : [...prev.categories, category],
    }))
  }

  // Set minimum rating (kept for backwards compatibility but not used)
  const setMinRating = (rating: number) => {
    setFilters((prev) => ({
      ...prev,
      minRating: rating,
    }))
  }

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      pricing: [],
      categories: [],
      minRating: 0,
      features: [],
    })
  }

  // Get active filter count (excluding minRating)
  const activeFilterCount =
    filters.pricing.length + filters.categories.length + filters.features.length

  return {
    filters,
    filteredTools,
    availableCategories,
    togglePricing,
    toggleCategory,
    setMinRating,
    clearFilters,
    activeFilterCount,
  }
}
